/**
 * One reading of the many pagination shapes the API returns.
 *
 * Routes grew their own `pagination` objects ({total} vs {totalCount},
 * {pages} vs {totalPages}, page-based vs skip-based). Read them through
 * `normalizePagination` instead of picking fields by hand, so a screen never
 * mistakes "this page" for "everything".
 *
 * The rules every list, search box and picker follows are in
 * docs/frontend-lists-and-search.md.
 */
export interface NormalizedPagination {
  /** Total matching records on the server, or null when the route does not say. */
  total: number | null;
  page: number;
  pageSize: number;
  totalPages: number | null;
  /** True when more records exist past this page. */
  hasMore: boolean;
}

/** The largest page the API serves on most list routes; asking for more is silently clamped. */
export const MAX_PAGE_SIZE = 100;

export function normalizePagination(raw: any, fallback: { page: number; pageSize: number; itemCount: number }): NormalizedPagination {
  const p = raw ?? {};
  const pageSize = num(p.limit) ?? num(p.pageSize) ?? fallback.pageSize;
  const page = num(p.page) ?? (num(p.skip) !== null && pageSize ? Math.floor(num(p.skip)! / pageSize) + 1 : null) ?? fallback.page;
  const total = num(p.total) ?? num(p.totalCount) ?? num(p.count);
  const totalPages = num(p.totalPages) ?? num(p.pages) ?? (total !== null && pageSize ? Math.max(1, Math.ceil(total / pageSize)) : null);
  const hasMore = typeof p.hasMore === 'boolean' ? p.hasMore
    : typeof p.hasNextPage === 'boolean' ? p.hasNextPage
    : totalPages !== null ? page < totalPages
    // No paging info at all: a full page may well have more behind it.
    : fallback.itemCount >= pageSize;
  return { total, page, pageSize, totalPages, hasMore };
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}
